/**
 * Resolves a lightbox group from the DOM given a clicked anchor.
 * Slide order follows DOM order of anchors with the same data-lightbox key.
 */
import { LIGHTBOX_TRIGGER_SELECTOR, type LightboxSlide } from "./lightboxContract";
import FileUtils from "@logic/FileUtils";

export interface ParseResult {
  groupKey: string;
  slides: LightboxSlide[];
  activeIndex: number;
}

export function parseLightboxGroup(clickedAnchor: HTMLAnchorElement): ParseResult | null {
  const key = clickedAnchor.getAttribute("data-lightbox");
  if (!key || !clickedAnchor.href) return null;

  const allAnchors = Array.from(document.querySelectorAll<HTMLAnchorElement>(LIGHTBOX_TRIGGER_SELECTOR));
  // querySelectorAll already returns nodes in document order.
  const groupAnchors = allAnchors.filter(anchor => anchor.getAttribute("data-lightbox") === key);

  const slides: LightboxSlide[] = groupAnchors.map((anchor, index) => anchorToSlide(anchor, index));
  const activeIndex = groupAnchors.findIndex(anchor => anchor === clickedAnchor);
  if (activeIndex < 0) return null;

  return {
    groupKey: key,
    slides,
    activeIndex,
  };
}

function anchorToSlide(anchor: HTMLAnchorElement, index: number): LightboxSlide {
  const href = anchor.href;
  const caption = anchor.getAttribute("data-caption") ?? undefined;
  const width = anchor.getAttribute("data-width") ?? undefined;
  const height = anchor.getAttribute("data-height") ?? undefined;
  const type = FileUtils.isVideoFileType(href, null) ? "video" : "image";

  const img = anchor.querySelector("img");
  const thumbFromImg = img?.src;

  let poster: string | undefined;
  if (type === "video") {
    poster = thumbFromImg;
  }

  const thumb = thumbFromImg ?? (type === "image" ? deriveThumbFromHref(href) : undefined);
  // Stamped by MediaThumbnail when it already cropped a tall photo for the social preview.
  const cropAttr = type === "image" ? anchor.getAttribute("data-crop-min-aspect") : null;
  const parsedCrop = cropAttr != null ? Number.parseFloat(cropAttr) : NaN;
  const cropMinAspectRatio = Number.isFinite(parsedCrop) ? parsedCrop : undefined;

  return {
    src: href,
    caption,
    type,
    poster,
    thumb,
    width,
    height,
    index,
    cropMinAspectRatio,
  };
}

function deriveThumbFromHref(href: string): string | undefined {
  try {
    const url = new URL(href);
    const path = url.pathname;
    const cdnPattern = /\/cms\/images\/expeditions\/(.+)$/;
    const match = path.match(cdnPattern);
    if (!match) return undefined;
    const fileName = match[1];
    if (fileName.startsWith("thumbtiny/") || fileName.startsWith("thumbmed/") || fileName.startsWith("thumbnocrop/")) return undefined;
    const thumbPath = path.replace(/\/cms\/images\/expeditions\//, "/cms/images/expeditions/thumbmed/");
    return `${url.origin}${thumbPath}`;
  } catch {
    return undefined;
  }
}
